/**
 * Professional mobile detection for complex web applications
 * Targets phone-sized screens that can't handle desktop interfaces
 * Allows tablets and larger devices to use the full interface
 */

export const isPhoneSizedDevice = (): boolean => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  // Never show mobile redirect in Figma plugin context
  if (
    window.parent !== window ||
    document.referrer.includes("figma.com") ||
    window.location.ancestorOrigins?.[0]?.includes("figma.com") ||
    navigator.userAgent.includes("Figma")
  ) {
    return false;
  }

  // Check if user has manually overridden (localStorage preference)
  const userOverride = localStorage.getItem("force-desktop-view");
  if (userOverride === "true") {
    return false;
  }

  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  // Industry standard breakpoints:
  // - Phones: up to 480px width
  // - Small tablets: 481-768px (allow these to use desktop)
  // - Tablets: 769-1024px (definitely allow these)
  // - Desktop: 1025px+ (definitely allow these)

  const isPhoneWidth = screenWidth <= 480;

  // Additional check: very narrow screens in portrait mode
  // This catches edge cases like narrow browser windows
  const isVeryNarrowPortrait = screenWidth <= 600 && screenHeight > screenWidth;

  // Check user agent for phone-specific patterns (excludes tablets)
  const userAgent = navigator.userAgent.toLowerCase();
  const isPhoneUserAgent =
    /iphone|ipod|android.*mobile|blackberry|opera mini|iemobile|windows phone/.test(
      userAgent,
    );

  // Only block on actual phone-sized screens
  return isPhoneWidth || (isVeryNarrowPortrait && isPhoneUserAgent);
};

/**
 * Paths that may render on phone-sized screens even for logged-in users.
 * Share-link ticket review and auth surfaces must stay reachable on phones.
 */
export function isMobileAllowedPath(pathname: string): boolean {
  if (pathname === "/login" || pathname.startsWith("/login/")) {
    return true;
  }
  return /^\/tickets\/[^/]+\/view\/?$/.test(pathname);
}

/**
 * Allow user to override mobile detection and use desktop version
 */
export const setDesktopViewPreference = (forceDesktop: boolean): void => {
  if (typeof localStorage !== "undefined") {
    if (forceDesktop) {
      localStorage.setItem("force-desktop-view", "true");
    } else {
      localStorage.removeItem("force-desktop-view");
    }
    // Reload to apply changes
    window.location.reload();
  }
};
